/**
 * The text renderer, and the reason it is not the default.
 *
 * No ESC/POS code page anybody's hardware ships contains Ө, Ү, ө or ү. CP866 —
 * the page this table is written for — has every other Cyrillic letter and not
 * those four, so text mode folds them onto О and У and «хүчил» prints «хучил».
 * Across a Mongolian menu that is most of the drinks.
 *
 * It stays available because raster is slower and larger, and on a unit where
 * that matters somebody has to be able to choose. `kiosk.example.toml`
 * documents the cost beside the switch.
 */
import type { Align, Slip } from './slip-model'

const ESC = 0x1b
const GS = 0x1d
const LF = 0x0a

export const CMD = {
  init: Uint8Array.from([ESC, 0x40]),
  alignLeft: Uint8Array.from([ESC, 0x61, 0x00]),
  alignCenter: Uint8Array.from([ESC, 0x61, 0x01]),
  alignRight: Uint8Array.from([ESC, 0x61, 0x02]),
  boldOn: Uint8Array.from([ESC, 0x45, 0x01]),
  boldOff: Uint8Array.from([ESC, 0x45, 0x00]),
  sizeNormal: Uint8Array.from([ESC, 0x21, 0x00]),
  sizeDouble: Uint8Array.from([ESC, 0x21, 0x30]),
  feed: (n: number) => Uint8Array.from([ESC, 0x64, n]),
  cut: Uint8Array.from([GS, 0x56, 0x01]),
  /** `ESC t n`. 17 is CP866, which is what the table below is written for. */
  codepage: (n: number) => Uint8Array.from([ESC, 0x74, n])
} as const

/**
 * `GS ( k` — the head encodes its own QR.
 *
 * Text mode does not use `qr.ts`. A printer that is being asked for text mode
 * is one where raster is too slow or too large, and sending it a bitmap of a QR
 * gives back exactly the cost that was being avoided. The head draws a sharper
 * code than a 203dpi raster of one anyway, because it lays out whole modules
 * instead of resampling them.
 */
export const QR = {
  /** Model 2, which is what every scanner and every e-Barimt reader expects. */
  model: Uint8Array.from([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00]),
  /** Module size in dots, 1-16. */
  size: (dots: number) => Uint8Array.from([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, Math.min(16, Math.max(1, dots))]),
  /** 48 L, 49 M, 50 Q, 51 H. M is e-Barimt's level and `qr.ts` explains why. */
  correction: (level: number) => Uint8Array.from([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, level]),
  /** Load the payload into the symbol-storage area. */
  store: (data: string) => {
    // The payload goes in as raw bytes, never through `cp866` — a QR carries
    // its own encoding and a receipt URL is ASCII either way.
    const bytes = new TextEncoder().encode(data)
    const length = bytes.length + 3
    const header = Uint8Array.from([GS, 0x28, 0x6b, length & 0xff, (length >>> 8) & 0xff, 0x31, 0x50, 0x30])
    const out = new Uint8Array(header.length + bytes.length)
    out.set(header)
    out.set(bytes, header.length)
    return out
  },
  print: Uint8Array.from([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30])
} as const

/**
 * CP866, by hand, for the range that matters.
 *
 * А-Я is 0x80-0x9F, а-п is 0xA0-0xAF, р-я is 0xE0-0xEF, Ё is 0xF0 and ё 0xF1.
 * Everything not here becomes `?`, deliberately: a silent substitution to a
 * lookalike is how a receipt comes out confidently wrong.
 */
/**
 * Typography with an exact ASCII equivalent.
 *
 * This is *not* the lookalike substitution the rule above forbids, and the
 * difference is whether meaning survives. Folding Ө onto О turns «хүчил» into a
 * different word. `×` has no CP866 slot either, but `x` says precisely what `×`
 * said — and without this the quantity column printed «Латте ?2», a question
 * mark on the one line where the number matters. `…` is here because `row()`
 * truncates with it, so every over-long label ended in `?`.
 *
 * Letters fold to `?`. Punctuation degrades to its ASCII spelling.
 */
const ASCII: Readonly<Record<string, number>> = {
  '×': 0x78, // x
  '·': 0x2d, // -
  '…': 0x2e, // .
  '—': 0x2d,
  '–': 0x2d,
  '\u2018': 0x27,
  '\u2019': 0x27,
  '\u201c': 0x22,
  '\u201d': 0x22
}

function cp866(char: string): number {
  const code = char.codePointAt(0) ?? 0x3f
  if (code < 0x80) return code
  if (code >= 0x0410 && code <= 0x042f) return code - 0x0410 + 0x80 // А-Я
  if (code >= 0x0430 && code <= 0x043f) return code - 0x0430 + 0xa0 // а-п
  if (code >= 0x0440 && code <= 0x044f) return code - 0x0440 + 0xe0 // р-я
  if (code === 0x0401) return 0xf0 // Ё
  if (code === 0x0451) return 0xf1 // ё
  if (code === 0x2116) return 0xfc // №
  return ASCII[char] ?? 0x3f
}

/** Which characters this encoding cannot carry. The settings screen names them. */
export function unrepresentable(text: string): string[] {
  const lost = new Set<string>()
  for (const char of text) {
    if (char.codePointAt(0)! >= 0x80 && cp866(char) === 0x3f) lost.add(char)
  }
  return [...lost]
}

export const encode = (text: string): Uint8Array => Uint8Array.from([...text].map(cp866))

/**
 * Pad a label and an amount to exactly `columns`, truncating the label rather
 * than wrapping.
 *
 * One space is always reserved between the two halves, so the label's budget is
 * `columns - right - indent - 1`. Getting that off by one produces a line one
 * character too long, which on a thermal printer wraps the amount onto its own
 * line — a total that appears to be zero.
 */
export function row(left: string, right: string, columns: number, indent = 0): string {
  const budget = Math.max(0, columns - right.length - indent - 1)
  const label = left.length > budget ? `${left.slice(0, Math.max(0, budget - 1))}…` : left
  const gap = Math.max(1, columns - indent - label.length - right.length)
  return `${' '.repeat(indent)}${label}${' '.repeat(gap)}${right}`
}

/**
 * The item table in a fixed-pitch face: quantity, unit price and amount in
 * right-aligned columns, and the name in whatever is left, wrapped.
 *
 * Below 40 columns there is no «whatever is left» worth having — a 58mm head
 * would give the name nine characters — so the name takes its own line and the
 * numbers go under it. One function for `renderText` and `previewLines`, so
 * what the console shows is what the head types.
 */
export function columnLines(cells: readonly [string, string, string, string], columns: number): string[] {
  const [name, quantity, unit, amount] = cells
  const numbers = `${quantity.padStart(4)}${unit.padStart(10)}${amount.padStart(10)}`
  if (columns < 40) {
    const pieces: string[] = []
    const chars = [...name]
    for (let at = 0; at < Math.max(1, chars.length); at += columns) pieces.push(chars.slice(at, at + columns).join(''))
    return [...pieces, numbers.padStart(columns)]
  }
  const budget = columns - numbers.length - 1
  const chars = [...name]
  const out: string[] = []
  for (let at = 0; at < Math.max(1, chars.length); at += budget) out.push(chars.slice(at, at + budget).join(''))
  return out.map((piece, at) => (at === 0 ? `${piece.padEnd(budget)} ${numbers}` : piece))
}

export interface TextOptions {
  /** Characters per line. 48 on 80mm at Font A, 32 on 58mm. */
  readonly columns: number
  /** The `ESC t` page. 17 is CP866. */
  readonly codepage: number
  /** QR module size in dots, 1-16. Six is ~30mm on an 80mm head. */
  readonly qrDots?: number
}

function concat(chunks: readonly Uint8Array[]): Uint8Array {
  const total = chunks.reduce((n, c) => n + c.length, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    out.set(chunk, offset)
    offset += chunk.length
  }
  return out
}

/** Store and print a QR at level M — e-Barimt's — followed by a feed. */
const qrCommands = (data: string, dots = 6): Uint8Array[] => [
  QR.model,
  QR.size(dots),
  QR.correction(49),
  QR.store(data),
  QR.print,
  Uint8Array.from([LF])
]

export function renderText(slip: Slip, options: TextOptions): Uint8Array {
  const out: Uint8Array[] = [CMD.init, CMD.codepage(options.codepage), CMD.alignLeft]
  const line = (text: string) => {
    out.push(encode(text), Uint8Array.from([LF]))
  }

  for (const block of slip.blocks) {
    switch (block.kind) {
      case 'text': {
        out.push(block.align === 'center' ? CMD.alignCenter : block.align === 'right' ? CMD.alignRight : CMD.alignLeft)
        if (block.bold === true) out.push(CMD.boldOn)
        if (block.size === 'large') out.push(CMD.sizeDouble)
        line(block.text)
        if (block.size === 'large') out.push(CMD.sizeNormal)
        if (block.bold === true) out.push(CMD.boldOff)
        out.push(CMD.alignLeft)
        break
      }
      case 'row': {
        if (block.bold === true) out.push(CMD.boldOn)
        line(row(block.left, block.right, options.columns, block.indent ?? 0))
        if (block.bold === true) out.push(CMD.boldOff)
        break
      }
      case 'rule':
        line((block.char ?? '-').repeat(options.columns))
        break
      case 'space':
        out.push(CMD.feed(block.lines ?? 1))
        break
      case 'number':
        out.push(CMD.alignCenter, CMD.sizeDouble, CMD.boldOn)
        line(block.label === undefined ? block.value : `${block.label}: ${block.value}`)
        out.push(CMD.boldOff, CMD.sizeNormal, CMD.alignLeft)
        break
      case 'qr':
        out.push(CMD.alignCenter, ...qrCommands(block.data, options.qrDots))
        if (block.caption !== undefined) line(block.caption)
        out.push(CMD.alignLeft)
        break
      case 'columns':
        for (const text of columnLines(block.cells, options.columns)) line(text)
        break
      /*
       * The head cannot set text beside a code it draws itself, so text mode
       * stacks them: the code centred, then each item as a row. The same facts
       * in the same order, which is what the customer needs off the paper.
       */
      case 'fiscalPanel':
        if (block.qr !== null) out.push(CMD.alignCenter, ...qrCommands(block.qr, options.qrDots), CMD.alignLeft)
        for (const item of block.items) {
          out.push(CMD.boldOn)
          line(row(item.label, item.value, options.columns))
          out.push(CMD.boldOff)
        }
        break
      case 'barcode':
        line(block.data)
        break
      /*
       * Text mode drops the masthead's art and keeps its lines, and says so
       * here rather than falling through.
       *
       * This renderer's whole reason to exist is that a shop measured raster as
       * too slow on its hardware; decoding a PNG and streaming `GS v 0` for it
       * would hand back the cost they opted out of. The console says the logo
       * is raster-only beside the upload, so this is a documented absence
       * rather than a silent one.
       */
      case 'header':
        if (block.lines.length === 0) break
        out.push(CMD.alignCenter)
        for (const text of block.lines) line(text)
        out.push(CMD.alignLeft)
        break
      default: {
        // The union is closed. Without this binding a new block kind compiles
        // and prints *nothing* — no error, no log line, just a receipt missing
        // a line nobody knew to look for. `html.ts` has had this guard from
        // the start; these two switches did not.
        const unhandled: never = block
        void unhandled
      }
    }
  }

  out.push(CMD.feed(3))
  if (slip.cut) out.push(CMD.cut)
  return concat(out)
}

export { concat as concatBytes }

/**
 * The same text, with everything CP866 cannot carry already turned into «?».
 *
 * `unrepresentable` answers *which* characters are lost; this answers *what the
 * paper says instead*, which is the question somebody arranging a receipt is
 * actually asking. Ө and Ү are the ones this fleet meets — see the header.
 */
export const asPrinted = (text: string): string =>
  [...text].map((char) => (char !== '?' && cp866(char) === 0x3f ? '?' : char)).join('')

/**
 * One line of paper, as text mode types it.
 *
 * `block` is the index of the {@link Slip} block that produced it, so a caller
 * holding `buildSlipWithOrigins`' ranges can say which *section* typed which
 * line — which is how the console shows an operator what one entry puts on the
 * paper without making them find it in the whole slip.
 */
export interface PreviewLine {
  readonly text: string
  readonly block: number
  /** Double width: twice the cell, half the characters to the line. */
  readonly double?: boolean
  /** The head draws this rather than typing it; `text` is its caption, if any. */
  readonly qr?: boolean
}

/** Break at the column count, because that is where the head breaks. */
const wrap = (text: string, width: number): string[] => {
  const chars = [...text]
  if (chars.length === 0) return ['']
  const out: string[] = []
  for (let at = 0; at < chars.length; at += width) out.push(chars.slice(at, at + width).join(''))
  return out
}

/**
 * Leading space for an alignment, and none trailing.
 *
 * A padded right-hand edge is invisible on paper and is a run of spaces in the
 * DOM, which is a preview whose lines are all exactly as wide as the paper
 * whether or not they have anything on them.
 */
const place = (text: string, width: number, align: Align | undefined): string => {
  const slack = Math.max(0, width - [...text].length)
  if (align === 'center') return `${' '.repeat(Math.floor(slack / 2))}${text}`
  if (align === 'right') return `${' '.repeat(slack)}${text}`
  return text
}

/**
 * What text mode prints, as lines somebody can read.
 *
 * {@link renderText} emits bytes; bytes are what a printer needs and what
 * nobody can check. This is the same walk over the same blocks producing the
 * same strings, with the two things the byte stream leaves to the hardware made
 * visible instead: **a character CP866 cannot carry is already «?»**, and **a
 * line longer than the paper is already wrapped**, because that is what the
 * head does with it. A 32-column shop discovers both here rather than on a
 * customer's receipt.
 *
 * It is a second walk over one union and that is a drift risk, so
 * `receipt.test.ts` holds the two together: every row this produces has to
 * appear in the bytes `renderText` writes for the same slip.
 */
export function previewLines(slip: Slip, columns: number): readonly PreviewLine[] {
  const out: PreviewLine[] = []
  const half = Math.max(1, Math.floor(columns / 2))

  const type = (text: string, block: number, width: number, align?: Align, double = false) => {
    for (const piece of wrap(asPrinted(text), width)) {
      out.push(
        double ? { text: place(piece, width, align), block, double } : { text: place(piece, width, align), block }
      )
    }
  }

  slip.blocks.forEach((block, at) => {
    switch (block.kind) {
      case 'text':
        type(block.text, at, block.size === 'large' ? half : columns, block.align, block.size === 'large')
        break
      case 'row':
        out.push({ text: asPrinted(row(block.left, block.right, columns, block.indent ?? 0)), block: at })
        break
      case 'rule':
        out.push({ text: (block.char ?? '-').repeat(columns), block: at })
        break
      case 'space':
        for (let n = 0; n < (block.lines ?? 1); n++) out.push({ text: '', block: at })
        break
      case 'number':
        type(block.label === undefined ? block.value : `${block.label}: ${block.value}`, at, half, 'center', true)
        break
      case 'columns':
        for (const text of columnLines(block.cells, columns)) out.push({ text: asPrinted(text), block: at })
        break
      case 'fiscalPanel':
        if (block.qr !== null) out.push({ text: '', block: at, qr: true })
        for (const item of block.items) out.push({ text: asPrinted(row(item.label, item.value, columns)), block: at })
        break
      case 'qr':
        out.push({ text: '', block: at, qr: true })
        if (block.caption !== undefined) type(block.caption, at, columns, 'center')
        break
      case 'barcode':
        type(block.data, at, columns)
        break
      /*
       * The lines and not the art: text mode drops the picture — `renderText`
       * says why. A placeholder line for it would be this preview inventing ink
       * the printer never lays down, which is the one thing a preview may not do.
       */
      case 'header':
        for (const text of block.lines) type(text, at, columns, 'center')
        break
      default: {
        const unhandled: never = block
        void unhandled
      }
    }
  })

  return out
}
